import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MenuItem {
  title: string;
  href: string;
  isHighlighted?: boolean;
}

const Lemoheader: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [navPos, setNavPos] = useState<'static' | 'absolute'>('static');
  const [headerBg, setHeaderBg] = useState<'white' | 'transparent'>('white');
  const menuRef = useRef<HTMLElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    const isStartups = location.pathname.includes('startups');
    setHeaderBg(isStartups ? 'transparent' : 'white');
    setNavPos(isStartups ? 'absolute' : 'static');

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 2);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menus: MenuItem[] = [
    { title: 'Startups', href: 'https://lemolite.com/startups', isHighlighted: true },
    { title: 'Services', href: 'https://lemolite.com/services' },
    { title: 'Tech Stack', href: 'https://lemolite.com/techstack' },
    { title: 'Company', href: 'https://lemolite.com/contact-us' },
  ];

  return (
    <nav
      ref={menuRef}
      className={`top-0 z-50 w-full pb-4 transition-all duration-500 md:p-0 ${
        isSticky
          ? 'sticky bg-white shadow-md backdrop-blur-md'
          : `relative ${headerBg === 'white' ? 'bg-white' : 'bg-transparent'} ${
              navPos === 'absolute' ? 'absolute' : 'static'
            }`
      }`}
    >
      <div className="container mx-auto flex items-center justify-between px-6 py-4 transition-all duration-500">
        <Link to="/" className="flex items-center">
          <img src="/logolemo.svg" alt="Lemolite Logo" width={150} height={50} className="object-contain" />
        </Link>

        <div className="hidden flex-1 justify-end lg:flex me-12">
          <ul className="flex items-center space-x-8 text-gray-700 font-medium">
            {menus.map((menu) => (
              <li key={menu.title}>
                <Link
                  to={menu.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-black font-semibold relative inline-block pb-1 transition-colors duration-200 ${menu.isHighlighted ? "is-highlighted" : ""}`}
                >
                  {menu.title}
                <span className="menu-underline" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="rounded text-gray-800 focus:outline-none lg:hidden"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>

      {isOpen && (
        <div className="mt-2 space-y-4 border-l border-gray-200 pl-4 pr-2 pt-2">
          <ul className="space-y-3">
            {menus.map((menu) => (
              <li key={menu.title}>
                <Link
                  to={menu.href}
                  onClick={() => setIsOpen(false)}
                  className="font-semibold capitalize text-[#212121]/80 transition hover:text-black"
                >
                  {menu.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Lemoheader;